import React from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { cn } from "@/lib/utils";
import {
  StickyNote, ScanLine, FileUp, Image as ImageIcon, Film, KeyRound, Bell,
  BookText, Mic, MessageSquare, Sparkles,
  TrendingUp, ShieldCheck, HeartPulse, Plane,
} from "lucide-react";

/**
 * Capture screen — Blueprint §5.
 * Prominent tiles for every save-flow. Falls back to Chat's attach for voice.
 */

const CAPTURES = [
  { key: "note",       label: "Note",         hint: "Quick thought",             icon: StickyNote, to: "/app/notes?new=1",     tone: "amber"   },
  { key: "scan",       label: "Scan document",hint: "Photo → PDF",             icon: ScanLine,   to: "/app/scanner",         tone: "sky"     },
  { key: "upload",     label: "Upload document", hint: "PDF or image",           icon: FileUp,     event: "impo:docs-upload", to: "/app/documents", tone: "emerald" },
  { key: "photo",      label: "Photo",        hint: "From gallery or camera",    icon: ImageIcon,  event: "impo:media-upload", to: "/app/media?tab=photos", tone: "purple" },
  { key: "video",      label: "Video",        hint: "Save a clip",               icon: Film,       event: "impo:media-upload", to: "/app/media?tab=videos", tone: "pink" },
  { key: "password",   label: "Password",     hint: "Secure vault",              icon: KeyRound,   to: "/app/passwords?new=1", tone: "indigo"  },
  { key: "reminder",   label: "Reminder",     hint: "Never forget",              icon: Bell,       to: "/app/reminders?new=1", tone: "orange"  },
  { key: "journal",    label: "Journal",      hint: "Reflect + AI-improve",      icon: BookText,   to: "/app/journal?new=1",   tone: "rose"    },
  { key: "voice",      label: "Voice note",   hint: "Speak, we transcribe",      icon: Mic,        to: "/app/voice-notes?new=1", tone: "slate"   },
  { key: "investment", label: "Investment",   hint: "Stocks, MFs, gold, crypto", icon: TrendingUp, to: "/app/investments?new=1", tone: "emerald2" },
  { key: "insurance",  label: "Insurance",    hint: "Policies & renewals",       icon: ShieldCheck,to: "/app/insurance?new=1", tone: "sky" },
  { key: "medical",    label: "Medical",      hint: "Reports & prescriptions",   icon: HeartPulse, to: "/app/medical?new=1",   tone: "rose2" },
  { key: "travel",     label: "Travel",       hint: "Trips, tickets, itineraries", icon: Plane,   to: "/app/travel?new=1",    tone: "indigo" },
];

const TONE = {
  amber:    "text-amber-500 bg-amber-500/12",
  sky:      "text-sky-500 bg-sky-500/12",
  emerald:  "text-emerald-500 bg-emerald-500/12",
  emerald2: "text-emerald-600 bg-emerald-600/12",
  purple:   "text-purple-500 bg-purple-500/12",
  pink:     "text-pink-500 bg-pink-500/12",
  indigo:   "text-indigo-500 bg-indigo-500/12",
  orange:   "text-orange-500 bg-orange-500/12",
  rose:     "text-rose-500 bg-rose-500/12",
  rose2:    "text-rose-600 bg-rose-600/12",
  slate:    "text-slate-500 bg-slate-500/12",
};

export default function Capture() {
  const nav = useNavigate();

  const handleClick = (item) => {
    if (item.event) {
      // Navigate first so the target route mounts before the event
      nav(item.to);
      // Fire the event next tick after route mounts
      setTimeout(() => window.dispatchEvent(new CustomEvent(item.event)), 300);
    } else {
      nav(item.to);
    }
  };

  return (
    <>
      <TopBar title="Capture" subtitle="Save anything important" showHome={false} />
      <div className="px-4 pt-3 pb-6 space-y-4" data-testid="capture-page">

        {/* Chat-first hero */}
        <button
          type="button"
          onClick={() => nav("/app/chat")}
          className="w-full rounded-3xl border border-border overflow-hidden relative text-left active:scale-[0.99] transition"
          data-testid="capture-chat-hero"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/15 to-primary/5" aria-hidden />
          <div className="relative p-4 flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-primary text-primary-foreground grid place-items-center shrink-0">
              <MessageSquare className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-primary font-medium">
                <Sparkles className="h-3 w-3" /> Fastest way
              </div>
              <div className="font-display font-bold text-[16px] leading-tight mt-0.5">Just tell Impo</div>
              <div className="text-[12px] text-muted-foreground mt-0.5">“Save my WiFi password” · “Scan this bill” · “Remind me tomorrow”</div>
            </div>
          </div>
        </button>

        <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground px-1">Or capture directly</div>

        <div className="grid grid-cols-2 gap-3" data-testid="capture-tiles">
          {CAPTURES.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => handleClick(c)}
                className="rounded-2xl border border-border bg-card p-4 flex flex-col items-start gap-2.5 hover:bg-[hsl(var(--accent))] transition text-left active:scale-[0.98]"
                data-testid={`capture-${c.key}`}
              >
                <div className={cn("h-11 w-11 rounded-xl grid place-items-center", TONE[c.tone])}>
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </div>
                <div className="w-full">
                  <div className="font-display font-semibold text-[14px] leading-tight">{c.label}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{c.hint}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
